import React from 'react';
import { AlertIcon } from './icons';
import type { Stage } from './api';

export const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const fmtNum = (n: number | null | undefined, places = 0): string =>
  n === null || n === undefined
    ? '—'
    : n.toLocaleString('en-ZA', { minimumFractionDigits: places, maximumFractionDigits: places });

export const plural = (n: number, one: string, many = `${one}s`) => `${fmtNum(n)} ${n === 1 ? one : many}`;

export const stageTone = (s: Stage): string =>
  s === 'Complete' ? 'is-good' : s === 'Slaughtered' ? '' : s === 'Being set up' ? 'is-quiet' : 'is-warn';

export const Pill: React.FC<{ tone?: string; children: React.ReactNode }> = ({ tone = '', children }) => (
  <span className={`pf-pill ${tone}`}>{children}</span>
);

export const Skeleton: React.FC<{ h?: number; w?: string | number; style?: React.CSSProperties }> = ({
  h = 56, w = '100%', style,
}) => <div className="pf-skel" style={{ height: h, width: w, ...style }} aria-hidden="true" />;

export const LoadingBlock: React.FC<{ rows?: number }> = ({ rows = 3 }) => (
  <div role="status" aria-label="Loading" className="pf-stack">
    {Array.from({ length: rows }, (_, i) => <Skeleton key={i} h={76} />)}
  </div>
);

export const ErrorBox: React.FC<{ message: string; onRetry?: () => void }> = ({ message, onRetry }) => (
  <div className="pf-stack" role="alert">
    <div className="pf-banner is-bad"><AlertIcon size={20} /><span>{message}</span></div>
    {onRetry && <button type="button" className="pf-btn is-ghost is-block" onClick={onRetry}>Try again</button>}
  </div>
);

export const Empty: React.FC<{ title: string; children?: React.ReactNode }> = ({ title, children }) => (
  <div className="pf-empty"><h3>{title}</h3>{children && <p>{children}</p>}</div>
);

/** Fetch-on-mount with retry; one place for loading/error so no screen invents its own.
 *  `key` is whatever identifies the request (a deal id, say): change it to reload. */
export function useLoad<T>(load: () => Promise<T>, key: string = '') {
  const loadRef = React.useRef(load);
  loadRef.current = load;
  const [state, setState] = React.useState<{ data: T | null; error: string | null; status: number | null; loading: boolean }>({
    data: null, error: null, status: null, loading: true,
  });
  const [tick, setTick] = React.useState(0);
  React.useEffect(() => {
    let live = true;
    setState(s => ({ ...s, loading: true, error: null }));
    loadRef.current().then(
      data => { if (live) setState({ data, error: null, status: null, loading: false }); },
      (e: any) => { if (live) setState({ data: null, error: e?.message || 'Something went wrong.', status: e?.status ?? null, loading: false }); },
    );
    return () => { live = false; };
  }, [key, tick]);
  return { ...state, retry: () => setTick(t => t + 1) };
}
